import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import BookingCardIcons from '../../components/BookingCardIcons';
import { getBookingCardHoverBackgroundColor } from './bookingCardHoverStyles';
import { useTheme } from '@mui/material/styles';

/**
 * Generic Regiondo (v1) booking card used for legacy external bookings.
 * Border is dashed, click opens Regiondo in new tab. Product and variation
 * names are displayed automatically.
 */
function RegiondoBookingCardBase({
  booking,
  color = '#ffffff',
  renderAvailableSlots = true,
  topOffset = renderAvailableSlots ? 40 : 16,
  rowHeight = 45,
  iconProps = {},
  children,
  // Horizontal scaling: width of a 30-minute slot in px (should match grid)
  slotWidthPx = 140,
}) {
  const {
    firstName,
    lastName,
    eventStart,
    eventDurationMS,
    participantCount,
    specialUpgrades,
    manualUpgrades,
    regiondoMeta,
    i,
  } = booking;
  const theme = useTheme();

  const openRegiondo = e => {
    // Prevent event from bubbling up from modal backdrops
    if (e.target.closest('.MuiModal-backdrop')) {
      return;
    }
    if (regiondoMeta?.booking_key) {
      window.open(
        'https://www.regiondo.de/resellers/account/bookingsview/key/' + regiondoMeta.booking_key,
        '_blank'
      );
    }
  };

  const eventStartDate = new Date(eventStart);
  const startHour = eventStartDate.getHours();
  const startMin = eventStartDate.getMinutes();
  const duration = eventDurationMS / 1000 / 60 / 60;
  const hourWidthPx = slotWidthPx * 2;
  const gridOriginPx = 27;
  const offset = (startHour - 9) * hourWidthPx + (startMin / 60) * hourWidthPx + gridOriginPx;
  const width = hourWidthPx * duration - 5;

  return (
    <Card
      variant='outlined'
      style={{
        position: 'absolute',
        left: offset,
        top: topOffset + (i - 1) * rowHeight,
        width,
        // Skip layout/paint for cards scrolled out of view so horizontal scroll
        // cost stays roughly constant regardless of booking count.
        contentVisibility: 'auto',
        containIntrinsicSize: `${Math.max(0, Math.round(Number(width) || 0))}px ${
          rowHeight - 6
        }px`,
        borderColor: color,
        zIndex: 2,
        cursor: 'pointer',
      }}
      sx={{
        '&:hover': {
          borderColor: color,
          backgroundColor: getBookingCardHoverBackgroundColor(color, theme),
        },
      }}
    >
      <CardContent sx={{ p: '2px 12px !important' }} onClick={openRegiondo}>
        <Stack direction='row' justifyContent='space-between' maxWidth={width}>
          <Typography
            component={'div'}
            sx={{
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              maxWidth: width,
              lineHeight: '100%',
            }}
          >
            <b>{firstName + ' ' + lastName}</b>{' '}
            <span style={{ fontSize: '80%' }}>
              {eventStartDate.toLocaleTimeString().substring(0, 5)}
              <br />
              {`${parseFloat(duration.toFixed(1))}h - ${regiondoMeta?.product_name}`}
            </span>
          </Typography>
          <BookingCardIcons
            upgrades={specialUpgrades}
            manualUpgrades={manualUpgrades}
            participantCount={participantCount}
            color={color}
            {...iconProps}
          />
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

RegiondoBookingCardBase.propTypes = {
  booking: PropTypes.object.isRequired,
  color: PropTypes.string,
  topOffset: PropTypes.number,
  iconProps: PropTypes.object,
  children: PropTypes.node,
};

export default RegiondoBookingCardBase;
